import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  LockClosedIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import { useState } from "react";
import { Button, EmptyState, Field, UserChip } from "@/components/ui";
import { useFeedback } from "@/components/ui/feedback";
import { Modal } from "@/components/ui/Modal";
import { MenuDivider, MenuItem, Popover } from "@/components/ui/Popover";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import type { Finance, Payment, PaymentReceipt } from "@/lib/types";
import { cn, formatDateTime, formatDay, formatMoney, formatPercent, moneyInput, parseMoney, pluralize, todayISO } from "@/lib/utils";
import { Money, MoneyInput, paymentStatus, splitAmount, StatusBadge, uncoveredExpenses, useFinanceRun } from "./common";

function PaymentForm({ fin, payment, onClose }: { fin: Finance; payment?: Payment; onClose: () => void }) {
  const { pid } = useProject();
  const run = useFinanceRun();
  const [title, setTitle] = useState(payment?.title ?? "");
  const [amount, setAmount] = useState(payment ? moneyInput(payment.amount) : "");
  const [dueDate, setDueDate] = useState(payment?.dueDate ?? "");
  const [note, setNote] = useState(payment?.note ?? "");
  const [saving, setSaving] = useState(false);
  const currency = fin.settings.currency;
  const cents = parseMoney(amount);
  const received = payment?.received ?? 0;
  const belowReceived = cents !== null && cents < received;
  const scheduled = fin.payments.filter((p) => p.id !== payment?.id).reduce((n, p) => n + p.amount, 0);
  const remaining = fin.settings.agreedAmount - scheduled;
  const valid = !!title.trim() && !!cents && !belowReceived;

  const submit = async () => {
    if (!valid || !cents) return;
    setSaving(true);
    const body = { title: title.trim(), amount: cents, dueDate: dueDate || null, note };
    const ok = await run(payment ? api.updatePayment(pid, payment.id, body) : api.createPayment(pid, body));
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={payment ? "Edit payment" : "Add a payment"}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} disabled={!valid} onClick={submit}>
            {payment ? "Save" : "Add payment"}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="Title">
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Deposit, Milestone 2, Final delivery" className="field" dir="auto" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Amount"
            hint={
              received > 0
                ? `${formatMoney(received, currency)} already received`
                : fin.settings.agreedAmount > 0 && remaining > 0
                  ? `${formatMoney(remaining, currency)} of the contract isn't scheduled yet`
                  : undefined
            }
            error={
              belowReceived
                ? `Can't be less than the ${formatMoney(received, currency)} already received`
                : amount && cents === null
                  ? "Enter an amount like 1500 or 1500.50"
                  : undefined
            }
          >
            <MoneyInput value={amount} onChange={setAmount} currency={currency} invalid={belowReceived || (!!amount && cents === null)} />
          </Field>
          <Field label="Due date" hint="Leave empty if it isn't scheduled yet.">
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="field" />
          </Field>
        </div>
        <Field label="Note">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="field resize-none" placeholder="Invoice number, conditions, what it covers…" dir="auto" />
        </Field>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

function ReceiveDialog({ fin, payment, onClose }: { fin: Finance; payment: Payment; onClose: () => void }) {
  const { pid, userName } = useProject();
  const run = useFinanceRun();
  const left = payment.amount - payment.received;
  const [amount, setAmount] = useState(moneyInput(left));
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const currency = fin.settings.currency;
  const cents = parseMoney(amount);
  const tooMuch = cents !== null && cents > left;
  const value = cents && !tooMuch ? cents : 0;
  const partial = value > 0 && value < left;
  const deducted = Math.min(uncoveredExpenses(fin), value);
  const distributable = value - deducted;
  const members = fin.currentShares;
  const parts = splitAmount(distributable, members);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Receive "${payment.title}"`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={LockClosedIcon}
            loading={saving}
            disabled={!date || !value}
            onClick={async () => {
              setSaving(true);
              const ok = await run(api.receivePayment(pid, payment.id, { amount: value, receivedOn: date }));
              setSaving(false);
              if (ok) onClose();
            }}
          >
            {partial ? "Receive part and lock split" : "Receive and lock split"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Amount received"
            hint={
              payment.received > 0
                ? `${formatMoney(left, currency)} left of ${formatMoney(payment.amount, currency)}`
                : "Lower it if the client paid only part of this payment."
            }
            error={tooMuch ? `More than the ${formatMoney(left, currency)} left on this payment` : amount && cents === null ? "Enter a valid amount" : undefined}
          >
            <MoneyInput value={amount} onChange={setAmount} currency={currency} autoFocus invalid={tooMuch || (!!amount && cents === null)} />
          </Field>
          <Field label="Received on">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="field" />
          </Field>
        </div>
        {partial && (
          <p className="text-sm text-muted">
            This is a partial payment. <Money value={left - value} layout="inline" /> will still be due, and you can receive it later.
          </p>
        )}
        <div className="rounded-lg border border-line text-sm">
          <div className="flex justify-between px-3 py-2">
            <span className="text-muted">Received now</span>
            <Money value={value} className="text-end" />
          </div>
          <div className="flex justify-between border-t border-line px-3 py-2">
            <span className="text-muted">Uncovered expenses taken out</span>
            <Money value={deducted} prefix={deducted > 0 ? "−" : ""} className="text-end" />
          </div>
          <div className="flex justify-between border-t border-line px-3 py-2 font-medium">
            <span>Split between members</span>
            <Money value={distributable} className="text-end" />
          </div>
        </div>
        {members.length > 0 ? (
          <div>
            <div className="mb-1.5 text-[13px] font-medium text-muted">Each member's cut, based on finished work right now</div>
            <div className="divide-y divide-line rounded-lg border border-line">
              {members.map((m, i) => (
                <div key={m.userId} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                  <UserChip id={m.userId} name={userName(m.userId)} className="min-w-0 flex-1" />
                  <span className="w-14 text-end text-muted tabular-nums">{formatPercent(m.share)}</span>
                  <Money value={parts[i]} className="w-36 text-end font-medium" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          distributable > 0 && (
            <p className="rounded-lg bg-[var(--tag-yellow-bg)] px-3 py-2 text-sm text-[var(--tag-yellow-fg)]">
              No tasks in a Done stage have assignees yet, so nobody would get a cut. You can receive it now and use <b>Recalculate split</b> later.
            </p>
          )
        )}
        {fin.members.some((m) => m.owes > 0) && (
          <p className="text-sm text-muted">
            Members who owe for expenses pay it back from their cut first:{" "}
            {fin.members
              .filter((m) => m.owes > 0)
              .map((m) => `${userName(m.userId)} owes ${formatMoney(m.owes, currency)}`)
              .join(", ")}
            .
          </p>
        )}
        <p className="text-xs text-faint">
          The split is saved with this receipt. Later work, level changes and withdrawals won't change it unless you recalculate it.
        </p>
      </div>
    </Modal>
  );
}

function ReceiptRow({ fin, receipt }: { fin: Finance; receipt: PaymentReceipt }) {
  const { pid, userName } = useProject();
  const run = useFinanceRun();
  const { confirm } = useFeedback();
  const [open, setOpen] = useState(false);
  const currency = fin.settings.currency;
  const unallocated = receipt.amount - receipt.expensesDeducted - receipt.distributed;
  const repayments = fin.repayments.filter((rp) => rp.receiptId === receipt.id);

  return (
    <div>
      <div className="flex items-center gap-1 pe-2">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-1.5 text-start text-xs text-muted hover:bg-hover"
          aria-expanded={open}
        >
          <ChevronRightIcon className={cn("size-4 transition-transform rtl:rotate-180", open && "rotate-90 rtl:rotate-90")} />
          <Money value={receipt.amount} layout="inline" className="font-medium text-fg" />
          <span>received {formatDay(receipt.receivedOn, true)}</span>
          <span className="flex items-center gap-1">
            <LockClosedIcon className="size-3" />
            <Money value={receipt.distributed} layout="inline" /> split between {pluralize(receipt.allocations.length, "member")}
            {receipt.expensesDeducted > 0 && <> · <Money value={receipt.expensesDeducted} layout="inline" /> to expenses</>}
          </span>
        </button>
        <Popover
          placement="bottom-end"
          className="w-52 p-1"
          trigger={(t) => (
            <button ref={t.ref} onClick={t.onClick} type="button" className="rounded p-1 text-faint hover:bg-hover hover:text-fg" aria-label="Receipt actions">
              <EllipsisHorizontalIcon className="size-4" />
            </button>
          )}
        >
          {(close) => (
            <>
              <MenuItem
                icon={ArrowPathIcon}
                onClick={async () => {
                  close();
                  const ok = await confirm({
                    title: "Recalculate this split?",
                    message:
                      "This receipt will be split again using today's finished tasks, experience levels and uncovered expenses. Members' earned amounts from it will change.",
                    confirmLabel: "Recalculate",
                  });
                  if (ok) run(api.recalculateReceipt(pid, receipt.id));
                }}
              >
                Recalculate split
              </MenuItem>
              <MenuItem
                icon={ArrowUturnLeftIcon}
                danger
                onClick={async () => {
                  close();
                  const ok = await confirm({
                    title: "Undo this receipt?",
                    message: `${formatMoney(receipt.amount, currency)} goes back to being due and its split is removed from members' balances. Withdrawals already recorded stay, so a balance can go negative.`,
                    confirmLabel: "Undo receipt",
                    danger: true,
                  });
                  if (ok) run(api.deleteReceipt(pid, receipt.id));
                }}
              >
                Undo receipt
              </MenuItem>
            </>
          )}
        </Popover>
      </div>
      {open && (
        <div className="divide-y divide-line border-t border-line bg-hover/40">
          {receipt.allocations.map((a) => (
            <div key={a.userId} className="flex items-center gap-3 px-3 py-1.5 ps-9 text-sm">
              <UserChip id={a.userId} name={userName(a.userId)} className="min-w-0 flex-1" />
              <span className="w-14 text-end text-muted tabular-nums">{formatPercent(a.share)}</span>
              <Money value={a.amount} className="w-36 text-end" />
            </div>
          ))}
          {receipt.allocations.length === 0 && <div className="px-3 py-2 ps-9 text-sm text-muted">Nobody had finished work when this was received. Recalculate to split it.</div>}
          {unallocated > 0 && receipt.allocations.length > 0 && <div className="px-3 py-1.5 ps-9 text-xs text-muted"><Money value={unallocated} layout="inline" /> wasn't allocated.</div>}
          {repayments.map((rp, i) => (
            <div key={i} className="px-3 py-1.5 ps-9 text-xs text-muted">
              {userName(rp.debtor)} paid back <Money value={rp.amount} layout="inline" className="text-fg" />{" "}
              {rp.creditor ? `to ${userName(rp.creditor)}` : "of an expense nobody could cover"}
            </div>
          ))}
          <div className="px-3 py-1.5 ps-9 text-xs text-faint">Locked {formatDateTime(receipt.lockedAt)}</div>
        </div>
      )}
    </div>
  );
}

function PaymentRow({ fin, payment, onEdit, onReceive }: { fin: Finance; payment: Payment; onEdit: () => void; onReceive: () => void }) {
  const { pid } = useProject();
  const run = useFinanceRun();
  const { confirm } = useFeedback();
  const currency = fin.settings.currency;
  const status = paymentStatus(payment, currency);
  const left = payment.amount - payment.received;
  const pct = Math.min(100, Math.round((payment.received / payment.amount) * 100));

  return (
    <li className="relative ps-8">
      <span className={cn("absolute start-[7px] top-4 size-3 rounded-full ring-4 ring-canvas", `dot-${status.color}`)} />
      <div className={cn("rounded-lg border border-line", status.color === "red" && "border-danger/40")}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5">
          <div className="min-w-0 flex-1 basis-full sm:basis-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate font-medium" dir="auto">
                {payment.title}
              </span>
              <StatusBadge status={status} />
            </div>
            <div className="mt-0.5 text-xs text-muted">{status.detail}</div>
          </div>
          <div className="me-auto sm:me-0 sm:text-end">
            <Money value={payment.amount} className="font-semibold" />
            {status.key === "partial" && <div className="text-xs text-muted">
                <Money value={payment.received} layout="inline" /> received
              </div>}
          </div>
          {left > 0 && (
            <Button size="sm" icon={CheckCircleIcon} onClick={onReceive}>
              Receive
            </Button>
          )}
          <Popover
            placement="bottom-end"
            className="w-56 p-1"
            trigger={(t) => (
              <button ref={t.ref} onClick={t.onClick} type="button" className="rounded p-1 text-muted hover:bg-hover" aria-label="Payment actions">
                <EllipsisHorizontalIcon className="size-5" />
              </button>
            )}
          >
            {(close) => (
              <>
                <MenuItem icon={PencilIcon} onClick={() => (close(), onEdit())}>
                  Edit
                </MenuItem>
                {left > 0 && (
                  <MenuItem icon={CheckCircleIcon} onClick={() => (close(), onReceive())}>
                    {payment.received > 0 ? "Receive more" : "Receive all or part"}
                  </MenuItem>
                )}
                <MenuDivider />
                <MenuItem
                  icon={TrashIcon}
                  danger
                  disabled={payment.receipts.length > 0}
                  onClick={async () => {
                    close();
                    if (await confirm({ title: `Delete "${payment.title}"?`, confirmLabel: "Delete", danger: true })) run(api.deletePayment(pid, payment.id));
                  }}
                >
                  {payment.receipts.length > 0 ? "Undo receipts to delete" : "Delete"}
                </MenuItem>
              </>
            )}
          </Popover>
        </div>
        {status.key === "partial" && (
          <div className="px-3 pb-2.5">
            <div className="h-1.5 overflow-hidden rounded-full bg-active" title={`${pct}% received`}>
              <div className="h-full rounded-full bg-[var(--dot-green)]" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        {payment.note && (
          <div className="border-t border-line px-3 py-2 text-sm whitespace-pre-wrap text-muted" dir="auto">
            {payment.note}
          </div>
        )}
        {payment.receipts.length > 0 && (
          <div className="divide-y divide-line border-t border-line">
            {payment.receipts.map((r) => (
              <ReceiptRow key={r.id} fin={fin} receipt={r} />
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

export function PaymentsTab({ fin }: { fin: Finance }) {
  const [editing, setEditing] = useState<Payment | "new" | null>(null);
  const [receiving, setReceiving] = useState<Payment | null>(null);
  const currency = fin.settings.currency;
  const scheduled = fin.payments.reduce((n, p) => n + p.amount, 0);
  const received = fin.payments.reduce((n, p) => n + p.received, 0);
  const agreed = fin.settings.agreedAmount;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted">
          <Money value={received} layout="inline" /> received of <Money value={scheduled} layout="inline" /> scheduled
          {agreed > 0 && (
            <>
              {" "}
              · contract <Money value={agreed} layout="inline" />
            </>
          )}
        </p>
        <Button variant="primary" icon={PlusIcon} onClick={() => setEditing("new")}>
          Add payment
        </Button>
      </div>
      {agreed > 0 && scheduled !== agreed && fin.payments.length > 0 && (
        <p className="rounded-lg border border-line px-3 py-2 text-sm text-muted">
          {scheduled < agreed
            ? `${formatMoney(agreed - scheduled, currency)} of the contract isn't in the schedule yet.`
            : `The schedule is ${formatMoney(scheduled - agreed, currency)} more than the contract.`}
        </p>
      )}
      {fin.payments.length === 0 ? (
        <EmptyState
          icon={BanknotesIcon}
          title="No payments scheduled"
          action={
            <Button variant="primary" icon={PlusIcon} onClick={() => setEditing("new")}>
              Add the first payment
            </Button>
          }
        >
          Split the contract into the payments the client will make, such as a deposit, milestones and a final payment, each with a due date. Each can be received
          in full or in parts.
        </EmptyState>
      ) : (
        <ol className="relative space-y-3 before:absolute before:start-[12px] before:top-5 before:bottom-5 before:w-px before:bg-line">
          {fin.payments.map((p) => (
            <PaymentRow key={p.id} fin={fin} payment={p} onEdit={() => setEditing(p)} onReceive={() => setReceiving(p)} />
          ))}
        </ol>
      )}
      {editing && <PaymentForm fin={fin} payment={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} />}
      {receiving && <ReceiveDialog fin={fin} payment={receiving} onClose={() => setReceiving(null)} />}
    </div>
  );
}
